import styled from "@emotion/styled";
import { MenuItem, Select, Stack } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Button } from "components/UI";
import { useChurchStore } from "features/churches";
import {
  useBudgetsPaginated,
  useBudgetYears,
  useFinanceSettingsStore
} from "features/finances";
import { FILTER_OPERATIONS } from "features/members";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { StatCard } from "../Cards";

const Container = styled.div`
  grid-column: 2 / 3;
  grid-row: 1 / 3;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const Title = styled.h6`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
`;

export const BudgetTable = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [budgetYear, setBudgetYear] = useState("");
  const [viewTotalBudget, setViewTotalBudget] = useState(true);

  const openSetBudgetModal = useFinanceSettingsStore(
    state => state.openSetBudgetModal
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const budgetYearsQuery = useBudgetYears({
    churchId: selectedChurch?.id
  });

  const budgetsQuery = useBudgetsPaginated({
    churchId: selectedChurch?.id,
    page,
    pageSize,
    queries: {
      filter: {
        budget_year_id: [
          {
            operator: FILTER_OPERATIONS.EQUAL,
            value: budgetYear
          }
        ]
      }
    },
    config: {
      enabled: !!budgetYear
    }
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (budgetYearsQuery.data?.result?.length > 0) {
      setBudgetYear(budgetYearsQuery.data?.result?.[0]?.id);
    }
  }, [budgetYearsQuery.data]);

  const columns = [
    {
      field: "id",
      headerName: "Department Name",
      flex: 1,
      renderCell: ({ row }) => <span>{row.team?.name}</span>
    },
    {
      field: "item_gross_amount",
      headerName: "Budget Assigned",
      flex: 1,
      valueFormatter: ({ value }) => {
        return Number(value).toLocaleString();
      }
    },
    {
      field: "budget_used",
      headerName: "Budget Used",
      flex: 1,
      valueFormatter: ({ value }) => {
        return Number(value).toLocaleString();
      }
    },
    {
      field: "budget_left",
      headerName: "Budget Left",
      flex: 1,
      valueFormatter: ({ value }) => {
        return Number(value).toLocaleString();
      }
    }
  ];

  return (
    <Container>
      <Header>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Title>Budget Year</Title>
          {budgetYearsQuery.data?.result?.length > 0 && (
            <Select
              value={budgetYear}
              onChange={e => setBudgetYear(e.target.value)}
              size="small"
            >
              {budgetYearsQuery.data?.result?.map(year => (
                <MenuItem key={year.id} value={year.id}>
                  {year.title}
                </MenuItem>
              ))}
            </Select>
          )}
        </Stack>
        <Button onClick={() => openSetBudgetModal()}>Set Budget</Button>
      </Header>
      <StatGrid>
        <StatCard
          title="Total Budget"
          stat={budgetsQuery.data?.result?.total_church_budget || 0}
          description="Total Budget"
          isVisible={viewTotalBudget}
          toggleVisibility={() => setViewTotalBudget(!viewTotalBudget)}
        />
      </StatGrid>
      <DataGrid
        autoHeight
        rows={budgetsQuery.data?.result?.data || []}
        rowCount={budgetsQuery.data?.result?.total || 0}
        loading={budgetsQuery.isLoading}
        columns={columns}
        pagination
        rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
        density="comfortable"
        getRowId={row => row.team?.id}
        disableColumnMenu
        disableSelectionOnClick
        paginationMode="server"
        page={page - 1}
        pageSize={pageSize}
        onPageChange={newPage => setPage(newPage + 1)}
        onPageSizeChange={newPageSize => setPageSize(newPageSize)}
        localeText={{
          noRowsLabel: t("teams.teamProfile.NoRows"),
          MuiTablePagination: {
            labelRowsPerPage: t("teams.teamProfile.RowsPerPage")
          }
        }}
        onRowClick={({ row }) => {
          navigate(
            `/finances/temp/budget/${row.team.id}?budget-year=${budgetYear}`
          );
        }}
      />
    </Container>
  );
};
